/**
 * Guest Orders Store
 *
 * Persists order-view tokens for account-less (guest) checkouts so the customer
 * can return to a read-only /orders/{token} view from this browser without the
 * emailed link or an account. Tokens are read-only bearer credentials to order
 * data — same exposure as the emailed link. Newest-first, deduped, capped.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SavedGuestOrder {
  token: string;
  bookingReference: string;
  label?: string;
  savedAt: string; // ISO timestamp
}

const MAX_SAVED = 20;

interface GuestOrdersState {
  orders: SavedGuestOrder[];
  promptDismissedFor: string | null;
  addOrder: (order: SavedGuestOrder) => void;
  removeOrder: (token: string) => void;
  dismissPrompt: (token: string) => void;
  clear: () => void;
}

export const useGuestOrdersStore = create<GuestOrdersState>()(
  persist(
    (set) => ({
      orders: [],
      promptDismissedFor: null,
      addOrder: (order) =>
        set((state) => {
          const rest = state.orders.filter((o) => o.token !== order.token);
          return { orders: [order, ...rest].slice(0, MAX_SAVED) };
        }),
      removeOrder: (token) =>
        set((state) => ({ orders: state.orders.filter((o) => o.token !== token) })),
      dismissPrompt: (token) => set({ promptDismissedFor: token }),
      clear: () => set({ orders: [], promptDismissedFor: null }),
    }),
    { name: 'stork-guest-orders', version: 1 }
  )
);
